import { useCallback, useEffect, useState } from "react";

import {
  type OnboardingState,
  useAirthingsStatus,
  useCompleteOnboardingStep,
  useSkipOnboardingStep,
} from "@/lib/api";
import { logger } from "@/lib/logger";

import { AirSensorsScreen } from "./air-sensors-screen";
import { AssistantProvider } from "./assistant-sheet";
import { MODULE_KEYS } from "./compartments";
import { FloorHeatingScreen } from "./floor-heating-screen";
import { FlowNavProvider } from "./flow-nav";
import { GreetingScreen } from "./greeting-screen";
import { HomeHubScreen } from "./home-hub-screen";
import { HubSetupScreen } from "./hub-setup-screen";
import { ModuleScreen } from "./module-screen";
import { NetworkSetupScreen } from "./network-setup-screen";
import { OffboardingScreen } from "./offboarding-screen";
import { MODULE_NAMES } from "./product-names";
import { VentilationScreen } from "./ventilation-screen";
import { WindowsScreen } from "./windows-screen";

interface FlowShellProps {
  state: OnboardingState;
}

function stepStatus(state: OnboardingState, key: string): string {
  return state.steps.find((step) => step.key === key)?.status ?? "pending";
}

function isDone(status: string): boolean {
  return status === "completed" || status === "skipped";
}

/**
 * The modules a household has finished, named the way the guide named them.
 *
 * Feeds the closing screen's "what you set up" recap, so the final screen lists
 * the compartments in the order the guide walked them and calls each one what
 * its card called it — a rename at the finish line would read as a different
 * product. `MODULE_KEYS` doubles as the filter: `network_setup` has no row of
 * its own (the guide folds it into the hub, and its screens are titled after
 * the hub), and `welcome`/`kit_confirmation`/`complete` are flow bookkeeping
 * rather than things anybody installed.
 *
 * `completed` only, not `isDone`: a skipped module is one the household chose
 * not to install, and handing it a green check on the closing recap would
 * claim credit for hardware that is still in the box.
 */
function completedModuleNames(state: OnboardingState): string[] {
  return MODULE_KEYS.flatMap((key) => {
    const step = state.steps.find((entry) => entry.key === key);
    return step && step.status === "completed" ? [MODULE_NAMES[key] ?? step.title] : [];
  });
}

/**
 * The modules a household skipped, for the same recap.
 *
 * Named separately rather than folded into the list above with a flag: the
 * closing screens render the two lists differently on purpose (green check
 * versus yellow, "what you set up" versus "not set up"), and VLX-76's finding
 * is that letting the two states share a rendering is how skips got invisible.
 */
function skippedModuleNames(state: OnboardingState): string[] {
  return MODULE_KEYS.flatMap((key) => {
    const step = state.steps.find((entry) => entry.key === key);
    return step && step.status === "skipped" ? [MODULE_NAMES[key] ?? step.title] : [];
  });
}

/**
 * How often to ask the AirThings cloud whether the sensors have come up.
 *
 * Deliberately slow. Each call costs two cloud round-trips server-side
 * (`get_devices` + `location_latest_samples`), and the answer we want changes
 * once, minutes after the tabs come out. 30s across compartments 3-5 is a few
 * dozen requests, and the poll stops on the first positive.
 */
const SENSOR_POLL_MS = 30_000;

/**
 * Routes the onboarding sequence: greeting → kit confirmation → Home Hub
 * (modules completable in any order) → completion.
 */
export function FlowShell({ state }: FlowShellProps) {
  return <FlowRoutes state={state} />;
}

/** The routed flow itself, with the assistant mounted around it. */
function FlowRoutes({ state }: FlowShellProps) {
  const completeStep = useCompleteOnboardingStep();
  const skipStep = useSkipOnboardingStep();
  const [saveError, setSaveError] = useState("");
  const [openModule, setOpenModule] = useState<string | null>(null);
  // The network + hub steps are forced (they gate everything else), but the user
  // can still back out to the guide to see where they are. Set while they're
  // looking; cleared as soon as they re-enter a module.
  const [showGuide, setShowGuide] = useState(false);
  // The closing task: Air Quality Part B, placing the sensors that have been
  // pairing since compartment 2. It runs after every compartment is done, and
  // completing it seals the flow.
  //
  // It is driven from here rather than from a step of its own because
  // `air_sensors` is a single persisted step key owned by the backend. Part A
  // completes that key, so ordering and the compartment card behave normally;
  // Part B hangs off the end of the flow instead, where the guide's finish
  // button used to be. Resuming works because a household that closed the app
  // after Part A returns with every compartment done and `complete` still
  // pending, which is exactly the state that opens this.
  const [closingTask, setClosingTask] = useState(false);
  /**
   * Re-open the network step after it has been completed.
   *
   * `network_setup` has no row on the guide (see MODULE_KEYS), so once it was
   * done there was no route back to it: back from the hub screen exited to the
   * guide, and a resident who miscounted their LAN ports was stuck on the wrong
   * branch for good. Completing again re-upserts the step, so a changed answer
   * overwrites the old payload rather than being ignored.
   */
  const [revisitNetwork, setRevisitNetwork] = useState(false);

  /**
   * Warm the sensor reading in the background, from the moment Part A ends.
   *
   * The sensors pair with their connector by sitting next to it, which takes
   * minutes. That wait used to happen with the household standing at a spinner;
   * splitting compartment 2 moved it off the critical path, and this finishes
   * the job by spending it on compartments 3-5 instead of at the end.
   *
   * It is deliberately NOT a gate. `/onboarding/airthings/status` answers
   * `connected: false` for four different reasons — unset AirThings
   * credentials, no building linked to the household, a cloud error, or a
   * sensor simply not sampled recently — and only the last means "not ready
   * yet". Blocking the closing task on this would leave a household with
   * unconfigured credentials unable to finish onboarding at all, and every
   * escape hatch (the 90s finish-anyway, the per-sensor deferral) lives inside
   * Part B, where they could no longer reach it. So the answer decorates the
   * row and nothing more.
   *
   * Shares `useAirthingsStatus`'s query key with the reading check, so warming
   * it here is exactly what makes that check instant later.
   */
  const airQualityApplies = state.steps.some((step) => step.key === "air_sensors");
  const partADone = isDone(stepStatus(state, "air_sensors"));
  const [sensorsReporting, setSensorsReporting] = useState(false);
  const sensorStatus = useAirthingsStatus({
    enabled: airQualityApplies && partADone && !sensorsReporting,
    refetchInterval: sensorsReporting ? undefined : SENSOR_POLL_MS,
  });
  useEffect(() => {
    // Latch: once they have reported we stop asking. A sensor dropping out of
    // the freshness window later is Part B's reading check to worry about, not
    // a reason to start polling again behind the user's back.
    if (sensorStatus.data?.connected) {
      logger.debug("[onboarding] air sensors reporting; background poll stopped");
      setSensorsReporting(true);
    }
  }, [sensorStatus.data?.connected]);

  // Back always lands on the guide. Both bits of state have to move together:
  // clearing `openModule` alone re-runs the forced network/hub guards, which
  // would bounce the user straight back into the screen they just left; setting
  // `showGuide` alone leaves `openModule` set, and the open-module branch below
  // would win. Either one on its own looks like a back button that does
  // nothing.
  const backToGuide = useCallback(() => {
    setOpenModule(null);
    setShowGuide(true);
  }, []);

  const openClosingTask = useCallback(() => {
    setOpenModule(null);
    setShowGuide(false);
    setClosingTask(true);
  }, []);

  /**
   * Record a compartment as skipped and hand the household back to the guide.
   *
   * `reason` is whatever error the screen was showing. It rides along to the
   * progress row because a skip is a support signal: knowing the module was
   * skipped is only half the story without knowing what failed.
   *
   * Returns whether the household actually moved. False covers both ways this
   * can decline: a skip already in flight, and a request that failed. Callers
   * that dismiss their own surface on the way out (the assistant sheet closes
   * itself) need to know, or they close over a step nobody left and the error
   * set here paints on a screen the resident can no longer see.
   */
  const skip = async (stepKey: string, reason: string): Promise<boolean> => {
    if (skipStep.isPending) {
      return false;
    }
    setSaveError("");
    logger.debug("[onboarding] skipping step", stepKey, reason);
    try {
      await skipStep.mutateAsync({ stepKey, reason });
      backToGuide();
      return true;
    } catch (err) {
      logger.warn("[onboarding] step skip failed:", err);
      setSaveError("Could not skip this step. Please try again.");
      return false;
    }
  };

  const complete = async (
    stepKey: string,
    onDone?: () => void,
    payload?: Record<string, unknown>,
  ) => {
    if (completeStep.isPending) {
      return;
    }
    setSaveError("");
    logger.debug("[onboarding] completing step", stepKey);
    try {
      await completeStep.mutateAsync({ stepKey, payload });
      onDone?.();
    } catch (err) {
      logger.warn("[onboarding] step completion failed:", err);
      setSaveError("Could not save your progress. Please try again.");
    }
  };

  const step = () => {
    // Fully done — the "complete" step seals the flow.
    if (state.current_step_key === null) {
      // No `onClose` on purpose: the screen ends the session and shows its own
      // goodbye in place. Routing anywhere would land a household who just
      // finished on the splash's "are you staff?" fork.
      return <OffboardingScreen skippedSteps={skippedModuleNames(state)} />;
    }

    if (closingTask) {
      return (
        <AirSensorsScreen
          part="b"
          rooms={state.rooms ?? []}
          // Part B ends the whole install, so its last screen recaps every
          // compartment rather than only the sensors it just placed.
          completedSteps={completedModuleNames(state)}
          skippedSteps={skippedModuleNames(state)}
          busy={completeStep.isPending}
          error={saveError}
          onComplete={() => complete("complete", () => setClosingTask(false))}
        />
      );
    }

    if (!isDone(stepStatus(state, "welcome"))) {
      return (
        <GreetingScreen
          firstName={state.first_name}
          busy={completeStep.isPending}
          error={saveError}
          onContinue={() => complete("welcome")}
        />
      );
    }

    // The guide/home screen is the landing right after the greeting (it
    // replaces the old "your kit includes…" screen — the module cards already
    // show what the kit contains). Only the hub is actionable here; tapping it
    // confirms the kit and drops into the forced network → hub setup, after
    // which the guide returns as the home base below.
    if (!isDone(stepStatus(state, "kit_confirmation"))) {
      return (
        <HomeHubScreen
          state={state}
          busy={completeStep.isPending}
          error={saveError}
          onOpenModule={() => complete("kit_confirmation")}
          onOpenClosingTask={openClosingTask}
          sensorsReporting={sensorsReporting}
          onFinish={() => complete("kit_confirmation")}
        />
      );
    }

    // Network prerequisite: confirm the router has LAN ports (or set up the
    // shipped switch + power strip) before the hardware modules.
    if (!showGuide && (revisitNetwork || !isDone(stepStatus(state, "network_setup")))) {
      return (
        <NetworkSetupScreen
          busy={completeStep.isPending}
          error={saveError}
          // Coming back to change an answer opens on the question itself, not
          // on the module intro three screens earlier.
          initialStage={revisitNetwork ? "check" : undefined}
          onComplete={(payload) =>
            complete("network_setup", () => setRevisitNetwork(false), payload)
          }
        />
      );
    }

    // Suggested path: after network setup we go straight into the Smart Climate
    // Hub (home_assistant) setup — the Home Hub module grid only appears once
    // the hub is connected, since the remaining modules aren't strictly
    // sequential. Until then, force the hub setup (unless the user explicitly
    // re-opened a module from the grid, which can only happen post-hub anyway).
    if (!showGuide && !openModule && !isDone(stepStatus(state, "home_assistant"))) {
      return (
        <HubSetupScreen
          busy={completeStep.isPending}
          error={saveError}
          onBackToNetwork={() => setRevisitNetwork(true)}
          onComplete={() => complete("home_assistant")}
        />
      );
    }

    const openStep = openModule ? state.steps.find((step) => step.key === openModule) : undefined;
    if (openStep) {
      // Modules with a designed sub-flow route to it; the rest keep the
      // placeholder until their Figma phases land.
      if (openStep.key === "home_assistant") {
        return (
          <HubSetupScreen
            busy={completeStep.isPending}
            error={saveError}
            onComplete={() => complete(openStep.key, () => setOpenModule(null))}
          />
        );
      }
      if (openStep.key === "air_sensors") {
        return (
          <AirSensorsScreen
            // Explicit even though "a" is the default: the reader needs to see
            // that opening compartment 2 runs only the first half, and that
            // Part B is the `closingTask` branch above.
            part="a"
            rooms={state.rooms ?? []}
            busy={completeStep.isPending}
            error={saveError}
            onComplete={() => complete(openStep.key, () => setOpenModule(null))}
          />
        );
      }
      {
        /* The hardware compartments take `onSkip`: each one can fail on
           something no one in the room can fix (an integration missing from the
           hub, a gateway that never provisions, a bus returning nothing), and
           the rest of the install sits behind it. The hub and network steps
           deliberately do NOT get one — they are prerequisites, so skipping
           them would only move the dead end one screen later. Air Quality
           already carries its own deferrals inside Part B.

           `SKIPPABLE_STEPS` in `escalation.ts` is this same list, for the
           assistant's escalation card: it has no screen to read it off, so it
           has to be told which steps have a way past them. Change one and
           change the other. */
      }
      if (openStep.key === "floor_heating") {
        return (
          <FloorHeatingScreen
            rooms={state.rooms ?? []}
            busy={completeStep.isPending}
            error={saveError}
            skipping={skipStep.isPending}
            onSkip={(reason) => skip(openStep.key, reason)}
            onComplete={(payload) => complete(openStep.key, () => setOpenModule(null), payload)}
          />
        );
      }
      if (openStep.key === "ventilation") {
        return (
          <VentilationScreen
            busy={completeStep.isPending}
            error={saveError}
            skipping={skipStep.isPending}
            onSkip={(reason) => skip(openStep.key, reason)}
            onComplete={() => complete(openStep.key, () => setOpenModule(null))}
          />
        );
      }
      if (openStep.key === "windows") {
        return (
          <WindowsScreen
            rooms={state.rooms ?? []}
            busy={completeStep.isPending}
            error={saveError}
            skipping={skipStep.isPending}
            onSkip={(reason) => skip(openStep.key, reason)}
            onComplete={(payload) => complete(openStep.key, () => setOpenModule(null), payload)}
          />
        );
      }
      return (
        <ModuleScreen
          step={openStep}
          busy={completeStep.isPending}
          error={saveError}
          onComplete={() => complete(openStep.key, () => setOpenModule(null))}
        />
      );
    }

    return (
      <HomeHubScreen
        state={state}
        busy={completeStep.isPending}
        error={saveError}
        onOpenModule={(stepKey) => {
          setSaveError("");
          setShowGuide(false);
          setOpenModule(stepKey);
        }}
        onOpenClosingTask={openClosingTask}
        sensorsReporting={sensorsReporting}
        onFinish={() => complete("complete")}
      />
    );
  };

  // Every screen below gets its back arrow from here, so a module screen only
  // has to handle stepping between its own stages.
  //
  // The assistant wraps the lot rather than sitting inside a screen: one
  // conversation has to survive moving between them, and its escalation card
  // needs the same `skip` the compartments use, so a problem the chat could
  // not solve leaves the same trail as a connector that never came online.
  return (
    <AssistantProvider onSkipStep={skip} skipping={skipStep.isPending}>
      <FlowNavProvider exitToGuide={backToGuide}>{step()}</FlowNavProvider>
    </AssistantProvider>
  );
}
